/** @deprecated 請改從 @curves/ui-shared 引入 UploadBox */
import React, { useImperativeHandle, useRef, useState, useCallback } from "react";
import { useSnackbar } from "notistack";
import clsx from "clsx";
import { v4 as uuid } from "uuid";
import img_cloud from "@icoach/assets/img/cloud.png";
import img_pdf from "@icoach/assets/img/file-pdf.png";
import img_image from "@icoach/assets/img/file-image.png";
import img_empty from "@icoach/assets/img/file-empty.png";
import img_nomatch from "@icoach/assets/img/file-nomatch.png";
import img_excel from "@icoach/assets/img/file-excel.png";
import { Icons, IconButton, FormHelperText, Typography, Box } from "@curves/ui-base";
import { linkDownload, isFunction } from "@util/utils";
import { PDFJS_WORKER_URL } from "@icoach/constants/appConfig";

let imgMap = {
	jpg: img_image,
	jepg: img_image,
	png: img_image,
	gif: img_image,
	bmp: img_image,
	pdf: img_pdf,
	xlsx: img_excel,
	csv: img_excel,
};

const ViewOnlyIcon = ({ extension, name }) => <img className="upload-box-img" src={imgMap[extension] || img_nomatch} alt={name} />;

// 僅顯示圖片檔案的元件
const ViewImageFileBox = ({ file, url, onClearFile, readOnly }) => {
	const fileObject = file || {};
	return (
		<picture className="image-wrap">
			{!readOnly && (
				<IconButton color="error" className="upload-box-close-icon" onClick={onClearFile}>
					<Icons.Close htmlColor="#fff" />
				</IconButton>
			)}
			{url ? <img className="upload-box-file-view" src={url} alt={fileObject.name} /> : <ViewOnlyIcon extension={fileObject.extension} name={fileObject.name} />}
			{fileObject && <Typography className="upload-box-file-icon">{`${fileObject.name}.${fileObject.extension}`}</Typography>}
		</picture>
	);
};

// 僅顯示 Excel/CSV/PDF 檔案的元件
const ViewExcelFileBox = ({ file, onClearFile, readOnly }) => {
	const fileObject = file || {};
	return (
		<picture className="image-wrap">
			{!readOnly && (
				<IconButton color="error" className="upload-box-close-icon" onClick={onClearFile}>
					<Icons.Close htmlColor="#fff" />
				</IconButton>
			)}
			<ViewOnlyIcon extension={fileObject.extension} name={fileObject.name} />
			{fileObject && <Typography className="upload-box-file-icon">{`${fileObject.name}.${fileObject.extension}`}</Typography>}
		</picture>
	);
};

const NoFileBox = props => {
	const { readOnly } = props;
	return (
		<React.Fragment>
			{!readOnly ? (
				<picture className="image-wrap">
					<img className="upload-box-img" src={img_cloud} alt="上傳檔案" />
					<Typography>{"請將要上傳的檔案拖曳至此或點擊此區域"}</Typography>
				</picture>
			) : (
				<picture className="image-wrap">
					<img className="upload-box-img" src={img_empty} alt="無檔案" />
					<Typography>{"無上傳檔案"}</Typography>
				</picture>
			)}
		</React.Fragment>
	);
};

const UploadBox = React.forwardRef((props, ref) => {
	const {
		link: linkProps = null,
		fileName: fileNameProps = "",
		className,
		onFileChange = () => {},
		onClearFile = () => {},
		accept = "*/*",
		fileExtension: fileExt,
		isError: propsError = false,
		helperText: helperTextProps = "此選項為必填，不得為空值",
		required = false,
		readOnly = false,
	} = props;
	const { enqueueSnackbar: _snackbar } = useSnackbar();
	const [fileState, setFile] = useState(null);
	const [linkState, setLink] = useState(linkProps);
	const [isError, setError] = useState(propsError);
	const [imagePreviewUrl, setImagePreviewUrl] = useState(null); // 前端預覽顯示，與API無關
	const fileRef = useRef(null);
	const divRef = useRef(null);

	// 清除檔案
	const handleClearFile = e => {
		e && e.preventDefault();
		e && e.stopPropagation();
		setFile(null);
		setLink(null);
		setImagePreviewUrl(null);
		isFunction(onClearFile) && onClearFile();
	};

	const handleClick = () => {
		if (linkState) linkDownload(linkState);
		else if (readOnly) return;
		else fileRef.current && fileRef.current.click();
	};

	const handleFileChange = useCallback(
		_file => {
			if (readOnly) return;
			setError(false);
			if (!_file) {
				console.error("No File unpload");
				return;
			}
			const [fileExtension, fileName] = _file.name.split(".").reverse();
			if (fileExt && !fileExt.includes(fileExtension)) {
				_snackbar(`${fileExtension}為錯誤的格式，請使用以下格式：${fileExt.join("、")}`, {
					variant: "error",
				});
				return;
			}
			const ext = fileExtension.toLowerCase();
			// ✅ 建立 FileReader
			const reader = new FileReader();
			reader.onprogress = event => {
				if (event.lengthComputable) {
					const percent = Math.round((event.loaded / event.total) * 100);
					console.log(percent);
				}
			};
			reader.onerror = () => {
				_snackbar("檔案讀取錯誤", { variant: "error" });
			};
			if (["jpg", "jpeg", "png", "gif", "bmp"].includes(ext)) {
				setReaderFileReloadEvent(reader, ext);
				reader.readAsDataURL(_file);
			} else if (["pdf"].includes(ext)) {
				setReaderFileReloadEvent(reader, ext);
				reader.readAsArrayBuffer(_file);
			} else if (["xlsx", "csv"].includes(ext)) {
				// 預留未來處理
				setReaderFileReloadEvent(reader, ext);
				reader.readAsArrayBuffer(_file);
			}
			setFile({
				extension: fileExtension,
				name: fileName,
				file: _file,
			});

			onFileChange && onFileChange(_file);
		},
		// eslint-disable-next-line
		[]
	);

	const handleOnDragEnter = e => {
		divRef.current.classList.add("upload-box-drop-enter");
		e.preventDefault();
		e.stopPropagation();
		if (readOnly) return;
	};

	const handleDragleave = e => {
		divRef.current.classList.remove("upload-box-drop-enter");
		e.preventDefault();
		e.stopPropagation();
		if (readOnly) return;
	};

	const handleDragover = e => {
		e.preventDefault();
		e.stopPropagation();
		if (readOnly) return;
	};

	const handleDrop = e => {
		e.preventDefault();
		e.stopPropagation();
		if (readOnly) return;
		const dt = e.dataTransfer;
		const files = dt.files;
		handleFileChange(files[0]);
		divRef.current.classList.remove("upload-box-drop-enter");
	};

	// 設定reader.onload
	const setReaderFileReloadEvent = async (reader, ext) => {
		if (["jpg", "jpeg", "png", "gif", "bmp"].includes(ext)) {
			reader.onload = async () => {
				setImagePreviewUrl(reader.result);
			};
		} else if (["xlsx", "csv"].includes(ext)) {
			// Allen todo
		} else if (["pdf"].includes(ext)) {
			reader.onload = async () => {
				try {
					const pdfjsLib = await import("pdfjs-dist/build/pdf");
					pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;

					const pdf = await pdfjsLib.getDocument({ data: reader.result }).promise;
					const page = await pdf.getPage(1);
					const viewport = page.getViewport({ scale: 0.5 });

					const canvas = document.createElement("canvas");
					const context = canvas.getContext("2d");
					canvas.width = viewport.width;
					canvas.height = viewport.height;

					await page.render({ canvasContext: context, viewport }).promise;
					const base64Thumbnail = canvas.toDataURL("image/png");

					setImagePreviewUrl(base64Thumbnail);
				} catch (error) {
					_snackbar("無效的檔案，請確認PDF檔案", { variant: "error" });
					handleClearFile();
					return;
				}
			};
		}
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => fileState,
			isError: () => {
				let isError = required;
				if (fileState) isError = false;
				setError(isError);
				return isError;
			},
		}),
		// eslint-disable-next-line
		[fileState]
	);

	return (
		<Box className={clsx("upload-box", "import-ach-box", className)} ref={divRef}>
			<Box component="input" className="file-input" type="file" ref={fileRef} onChange={e => handleFileChange(e.target.files[0])} accept={accept} key={uuid()} />
			<Box className="upload-box-wrap" onClick={handleClick} onDragEnter={handleOnDragEnter} onDragLeave={handleDragleave} onDragOver={handleDragover} onDrop={handleDrop}>
				<Box className={clsx("upload-box-content", "text-center")} onDragEnter={handleOnDragEnter}>
					{fileState || linkState ? (
						(() => {
							// 前端物件拆解(前端操作時的動作，尚未送出) ||後端檔名拆解(讀取該合約資料來源)
							const ext = fileState?.extension?.toLowerCase() || linkState.match(/.([^./]+)$/)[1];
							const f = {
								name: fileState?.name ?? fileNameProps,
								extension: ext,
							};
							if (["jpg", "jpeg", "png", "gif", "bmp", "pdf"].includes(ext)) {
								return <ViewImageFileBox file={f} url={imagePreviewUrl || linkState} onClearFile={handleClearFile} readOnly={readOnly} />;
							}
							return <ViewExcelFileBox file={f} onClearFile={handleClearFile} readOnly={readOnly} />;
						})()
					) : (
						<NoFileBox readOnly={readOnly} />
					)}
					{fileExt && <Typography className="desc">支援檔案格式：{fileExt.join("、")}</Typography>}
				</Box>
			</Box>
			{isError && <FormHelperText isError={isError}>{helperTextProps}</FormHelperText>}
		</Box>
	);
});

export default UploadBox;
